import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Text,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiCopy,
  FiDownload,
  FiMessageSquare,
  FiPhoneOff,
  FiRefreshCw,
  FiSearch,
  FiUser,
  FiUserX,
} from "react-icons/fi";
import { getSupabase, supabasePublic, supabaseSchema } from "../supabaseClient";
import { tableName } from "../env";
import {
  FAILED_NUMBER_STATUSES,
  errorCodeCounts,
  failedNumberStatus,
  failedNumbersCsv,
  filterFailedNumbers,
  formatPhone,
  formatVisitDate,
  phoneVariants,
  recommendedAction,
  summarizeFailedNumbers,
  timeAgo,
} from "../../utils/failedNumbers";
import { EMPTY_RANGE, filterByDate } from "../../utils/dateRange";
import DateRangeFilter from "./DateRangeFilter";

const PAGE = 1000; // PostgREST's default max rows per request
const ID_CHUNK = 100; // keeps the `id=in.(...)` URL well under length limits
const SHOW_STEP = 25;

// sms_number_health stores only "notes-c" ids (it is readable across clinics);
// names are read here, from each provider's own table. A failure only costs
// that provider's names, never the list.
export const attachPatients = async (rows) => {
  const idsBySchema = new Map();
  rows.forEach((r) => {
    if (!idsBySchema.has(r.schema_name)) idsBySchema.set(r.schema_name, new Set());
    (r.patient_ids || []).forEach((id) => idsBySchema.get(r.schema_name).add(id));
  });
  const byKey = {};
  const supabase = await getSupabase().catch(() => null);
  await Promise.all(
    [...idsBySchema.entries()].map(async ([schema, idSet]) => {
      const ids = [...idSet];
      try {
        for (let i = 0; i < ids.length; i += ID_CHUNK) {
          const { data, error } = await supabase
            .schema(schema)
            .from(tableName)
            .select("id, patient_name, date_")
            .in("id", ids.slice(i, i + ID_CHUNK));
          if (error) throw error;
          (data || []).forEach((p) => {
            byKey[`${schema}:${p.id}`] = {
              id: String(p.id),
              name: (p.patient_name || "").trim(),
              lastVisit: p.date_,
            };
          });
        }
      } catch (e) {
        console.warn(`[Failed numbers] could not load patient names for "${schema}":`, e?.message || e);
      }
    }),
  );
  return rows.map((r) => {
    const patients = (r.patient_ids || [])
      .map((id) => byKey[`${r.schema_name}:${id}`])
      .filter((p) => p && p.name);
    return { ...r, patients, patient_names: patients.map((p) => p.name) };
  });
};

// Every number in the selected providers that has failed at least once, or is
// on the bad/opt-out list. Rebuilt from "SMS" by pg_cron every 5 minutes.
export const loadFailedNumbers = async (schemas) => {
  const client = supabasePublic();
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await client
      .from("sms_number_health")
      .select("*")
      .in("schema_name", schemas)
      .or("failed_count.gt.0,verdict.in.(bad,opt_out)")
      .order("last_failure_at", { ascending: false, nullsFirst: false })
      .order("schema_name", { ascending: true })
      .order("phone_number", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  const [{ data: codes }, withPatients] = await Promise.all([
    client.from("sms_error_codes").select("error_code, description, severity, category"),
    attachPatients(rows),
  ]);
  return { rows: withPatients, codes: codes || [] };
};

export const loadRecentTexts = async (phone, schema) => {
  const supabase = await getSupabase();
  const { data, error } = await supabase
    .schema(schema)
    .from("SMS")
    .select("id, created_at, message, status, error_code, error_message")
    .in("to", phoneVariants(phone))
    .order("created_at", { ascending: false })
    .limit(5);
  if (error) throw error;
  return data || [];
};

// The migration has not been applied to this database yet.
export const isMissingTable = (error) =>
  ["42P01", "PGRST205"].includes(error?.code) || /sms_number_health/.test(error?.message || "");

const STATUS_ICONS = {
  bad: FiPhoneOff,
  opt_out: FiUserX,
  failing: FiAlertTriangle,
  recovered: FiCheckCircle,
};

const FILTERS = [
  { key: "attention", label: "Needs attention" },
  { key: "bad", label: "Bad number" },
  { key: "opt_out", label: "Opted out" },
  { key: "failing", label: "Failing" },
  { key: "recovered", label: "Recovered" },
  { key: "all", label: "All" },
];

export const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const StatTile = ({ label, value, hint, colorScheme, icon, isActive, onClick }) => {
  const bg = useColorModeValue(`${colorScheme}.50`, "whiteAlpha.50");
  const border = useColorModeValue(`${colorScheme}.100`, "whiteAlpha.200");
  const accent = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.300`);
  return (
    <Box
      as={onClick ? "button" : "div"}
      onClick={onClick}
      textAlign="left"
      bg={bg}
      borderWidth="1px"
      borderColor={isActive ? accent : border}
      boxShadow={isActive ? `0 0 0 1px var(--chakra-colors-${colorScheme}-400)` : "none"}
      borderRadius="xl"
      px={3}
      py={2.5}
      transition="all 0.15s"
      _hover={onClick ? { transform: "translateY(-1px)", boxShadow: "sm" } : undefined}
    >
      <HStack spacing={1.5} color={accent}>
        <Icon as={icon} boxSize={3.5} />
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
          {label}
        </Text>
      </HStack>
      <Text fontSize="2xl" fontWeight="bold" lineHeight="short" mt={1}>
        {value.toLocaleString()}
      </Text>
      {hint && (
        <Text fontSize="xs" color="gray.500" noOfLines={1}>
          {hint}
        </Text>
      )}
    </Box>
  );
};

export const Fact = ({ label, children }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {children}
    </Text>
  </Box>
);

export const RecentTexts = ({ phone, schema, load }) => {
  const [texts, setTexts] = useState(null);
  const [error, setError] = useState(null);
  const bubble = useColorModeValue("gray.50", "whiteAlpha.100");

  useEffect(() => {
    let cancelled = false;
    load(phone, schema)
      .then((data) => !cancelled && setTexts(data))
      .catch((e) => !cancelled && setError(e.message || "Could not load texts"));
    return () => {
      cancelled = true;
    };
  }, [phone, schema, load]);

  if (error) {
    return (
      <Text fontSize="sm" color="red.500">
        {error}
      </Text>
    );
  }
  if (!texts) {
    return (
      <Stack spacing={2}>
        <Skeleton h="36px" borderRadius="md" />
        <Skeleton h="36px" borderRadius="md" />
      </Stack>
    );
  }
  if (texts.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        No texts found for this number.
      </Text>
    );
  }
  return (
    <Stack spacing={2} data-testid="recent-texts">
      {texts.map((t) => {
        const failed = t.error_code || ["failed", "undelivered"].includes((t.status || "").toLowerCase());
        return (
          <Box key={t.id} bg={bubble} borderRadius="md" px={3} py={2}>
            <Flex justify="space-between" align="center" gap={2} mb={1}>
              <Text fontSize="xs" color="gray.500">
                {formatDateTime(t.created_at)}
              </Text>
              <HStack spacing={1}>
                {t.error_code && (
                  <Badge colorScheme="red" variant="subtle" fontSize="2xs">
                    {t.error_code}
                  </Badge>
                )}
                <Badge colorScheme={failed ? "red" : "green"} variant="subtle" fontSize="2xs">
                  {t.status || "sent"}
                </Badge>
              </HStack>
            </Flex>
            <Text fontSize="sm" noOfLines={2}>
              {t.message || <Text as="span" color="gray.400">(no text)</Text>}
            </Text>
          </Box>
        );
      })}
    </Stack>
  );
};

const NumberCard = ({ row, codes, isOpen, onToggle, loadTexts, providerLabel }) => {
  const toast = useToast();
  const status = failedNumberStatus(row);
  const meta = FAILED_NUMBER_STATUSES[status];
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const iconBg = useColorModeValue(`${meta.colorScheme}.50`, "whiteAlpha.100");
  const iconColor = useColorModeValue(`${meta.colorScheme}.500`, `${meta.colorScheme}.300`);
  const phone = formatPhone(row.phone_number);
  const names = row.patient_names || [];
  const description =
    row.error_description || codes[row.last_error_code]?.description || row.last_error_message;

  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(row.phone_number);
      toast({ title: `Copied ${phone}`, status: "success", duration: 1500 });
    } catch {
      toast({ title: "Could not copy", status: "error", duration: 2000 });
    }
  };

  return (
    <Box
      data-testid="failed-number"
      bg={cardBg}
      borderWidth="1px"
      borderColor={isOpen ? `${meta.colorScheme}.300` : border}
      borderRadius="xl"
      overflow="hidden"
      transition="border-color 0.15s"
    >
      <Flex
        as="button"
        type="button"
        w="100%"
        textAlign="left"
        align="center"
        gap={3}
        px={3}
        py={2.5}
        onClick={onToggle}
        _hover={{ bg: hoverBg }}
        aria-expanded={isOpen}
      >
        <Center boxSize={9} borderRadius="full" bg={iconBg} color={iconColor} flexShrink={0}>
          <Icon as={STATUS_ICONS[status]} boxSize={4} />
        </Center>
        <Box flex="1" minW={0}>
          <HStack spacing={2} wrap="wrap">
            {names.length ? (
              <Text fontWeight="semibold" fontSize="sm" noOfLines={1} data-testid="patient-name">
                {names[0]}
                {names.length > 1 && (
                  <Text as="span" fontWeight="normal" color="gray.500">
                    {" "}
                    +{names.length - 1} more
                  </Text>
                )}
              </Text>
            ) : (
              <Text fontWeight="semibold" fontFamily="mono" fontSize="sm">
                {phone}
              </Text>
            )}
            <Badge colorScheme={meta.colorScheme} variant="subtle" borderRadius="full" px={2}>
              {meta.label}
            </Badge>
            {providerLabel && (
              <Badge variant="outline" colorScheme="gray" borderRadius="full" px={2} data-testid="provider">
                {providerLabel}
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {names.length > 0 && (
              <Text as="span" fontFamily="mono">
                {phone} ·{" "}
              </Text>
            )}
            {row.last_error_code ? (
              <>
                <Text as="span" fontWeight="semibold">
                  {row.last_error_code}
                </Text>
                {description ? ` · ${description}` : ""}
              </>
            ) : row.on_do_not_text ? (
              "On the Do Not Text list"
            ) : (
              description || "Delivery failed"
            )}
          </Text>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Text fontSize="sm" fontWeight="semibold">
            {row.failed_count} failed
          </Text>
          <Text fontSize="xs" color="gray.500">
            {timeAgo(row.last_failure_at)}
          </Text>
        </Box>
        <Icon
          as={FiChevronDown}
          color="gray.400"
          transition="transform 0.2s"
          transform={isOpen ? "rotate(180deg)" : "none"}
        />
      </Flex>

      <Collapse in={isOpen} animateOpacity unmountOnExit>
        <Box px={3} pb={3} pt={1} borderTopWidth="1px" borderColor={border}>
          <Alert
            status={status === "recovered" ? "success" : status === "failing" ? "warning" : "error"}
            variant="left-accent"
            borderRadius="md"
            mt={2}
            py={2}
          >
            <AlertIcon />
            <AlertDescription fontSize="sm">{recommendedAction(row)}</AlertDescription>
          </Alert>

          <Box mt={3}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
              {names.length > 1 ? "Patients with this number" : "Patient"}
            </Text>
            {names.length ? (
              <Wrap spacing={2}>
                {row.patients.map((p) => (
                  <WrapItem key={p.id}>
                    <HStack spacing={2} borderWidth="1px" borderColor={border} borderRadius="full" pl={2} pr={3} py={1}>
                      <Icon as={FiUser} color="gray.400" boxSize={3.5} />
                      <Text fontSize="sm" fontWeight="medium">
                        {p.name}
                      </Text>
                      {p.lastVisit && (
                        <Text fontSize="xs" color="gray.500">
                          last visit {formatVisitDate(p.lastVisit)}
                        </Text>
                      )}
                    </HStack>
                  </WrapItem>
                ))}
              </Wrap>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No patient on file has this number. It may already have been corrected.
              </Text>
            )}
          </Box>

          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mt={3}>
            <Fact label="Texts">
              {row.delivered_count} delivered · {row.failed_count} failed · {row.total_messages} total
            </Fact>
            <Fact label="Last failure">{formatDateTime(row.last_failure_at)}</Fact>
            <Fact label="Last delivered">{formatDateTime(row.last_success_at)}</Fact>
            <Fact label="First texted">{formatDateTime(row.first_seen_at)}</Fact>
            <Fact label="Do Not Text">{row.on_do_not_text ? "Yes" : "No"}</Fact>
          </SimpleGrid>

          {(row.distinct_error_codes || []).length > 0 && (
            <Box mt={3}>
              <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
                Error codes seen
              </Text>
              <Stack spacing={1.5}>
                {row.distinct_error_codes.map((code) => (
                  <HStack key={code} align="start" spacing={2}>
                    <Badge
                      colorScheme={code === row.last_error_code ? meta.colorScheme : "gray"}
                      fontFamily="mono"
                      flexShrink={0}
                    >
                      {code}
                    </Badge>
                    <Text fontSize="sm">
                      {codes[code]?.description || "Unmapped code — see Twilio's error reference"}
                      {code === row.last_error_code && (
                        <Text as="span" color="gray.500">
                          {" "}
                          (most recent)
                        </Text>
                      )}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            </Box>
          )}

          <Box mt={3}>
            <HStack spacing={1.5} mb={1.5} color="gray.500">
              <Icon as={FiMessageSquare} boxSize={3} />
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="wide">
                Recent texts
              </Text>
            </HStack>
            <RecentTexts phone={row.phone_number} schema={row.schema_name} load={loadTexts} />
          </Box>

          <Flex justify="flex-end" mt={3}>
            <Button size="xs" variant="outline" leftIcon={<FiCopy />} onClick={copy}>
              Copy number
            </Button>
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

// "<schema> (home)" for the viewer's own schema, as ProvidersDropdown labels it.
export const providerName = (schema, homeSchema) => (schema === homeSchema ? `${schema} (home)` : schema);

const FailedNumbersPanel = ({
  isActive = true,
  schemas,
  homeSchema,
  loadData = loadFailedNumbers,
  loadTexts = loadRecentTexts,
}) => {
  const providers = useMemo(
    () => (schemas && schemas.length ? schemas : [supabaseSchema || "public"]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(schemas || []).join(",")],
  );
  const home = homeSchema || supabaseSchema;
  const [provider, setProvider] = useState("");
  const [state, setState] = useState({ status: "idle", rows: [], codes: {}, error: null });
  const [filter, setFilter] = useState("attention");
  const [errorCode, setErrorCode] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [dates, setDates] = useState(EMPTY_RANGE);
  const [openKey, setOpenKey] = useState(null);
  const [shown, setShown] = useState(SHOW_STEP);
  const muted = useColorModeValue("gray.600", "gray.400");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: s.rows.length ? "refreshing" : "loading", error: null }));
    try {
      const { rows, codes } = await loadData(providers);
      const codeMap = Object.fromEntries((codes || []).map((c) => [c.error_code, c]));
      setState({ status: "ready", rows, codes: codeMap, error: null });
    } catch (error) {
      setState((s) => ({ ...s, status: isMissingTable(error) ? "missing" : "error", error }));
    }
  }, [providers, loadData]);

  // Load the first time the tab is opened, not when the modal mounts.
  useEffect(() => {
    if (isActive && state.status === "idle") load();
  }, [isActive, state.status, load]);

  useEffect(() => setShown(SHOW_STEP), [filter, errorCode, search, sort, provider, dates]);

  const { rows: allRows, codes } = state;
  // Providers that actually have failed numbers; the filter is only offered
  // when there is more than one to choose from.
  const rowProviders = useMemo(
    () => [...new Set(allRows.map((r) => r.schema_name))].sort(),
    [allRows],
  );
  const multiProvider = rowProviders.length > 1;
  // The date range narrows the tiles and chips too, not just the list.
  const rows = useMemo(
    () => filterByDate(provider ? allRows.filter((r) => r.schema_name === provider) : allRows, "last_failure_at", dates),
    [allRows, provider, dates],
  );
  const summary = useMemo(() => summarizeFailedNumbers(rows), [rows]);
  const codeOptions = useMemo(() => errorCodeCounts(rows), [rows]);
  const visible = useMemo(
    () => filterFailedNumbers(rows, { status: filter, errorCode, search, sort }),
    [rows, filter, errorCode, search, sort],
  );
  const lastSynced = useMemo(
    () => allRows.reduce((max, r) => (r.refreshed_at > max ? r.refreshed_at : max), ""),
    [allRows],
  );

  const exportCsv = () => {
    const blob = new Blob([failedNumbersCsv(visible)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `failed-numbers-${provider || providers.join("+")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (state.status === "idle" || state.status === "loading") {
    return (
      <Center py={10} flexDirection="column" gap={3}>
        <Spinner size="lg" color="teal.500" />
        <Text color={muted}>Loading failed numbers…</Text>
      </Center>
    );
  }

  if (state.status === "missing") {
    return (
      <Alert status="info" borderRadius="lg" alignItems="start">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Failed-number tracking isn't set up on this database yet. Run{" "}
          <Text as="span" fontFamily="mono">
            sql/migrations/2026-09-22-sms-number-health.sql
          </Text>{" "}
          in Supabase, then reopen this tab.
        </AlertDescription>
      </Alert>
    );
  }

  if (state.status === "error") {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertDescription fontSize="sm" flex="1">
          Could not load failed numbers: {state.error?.message || "unknown error"}
        </AlertDescription>
        <Button size="sm" onClick={load}>
          Try again
        </Button>
      </Alert>
    );
  }

  if (allRows.length === 0) {
    return (
      <Center py={10} flexDirection="column" gap={2} textAlign="center" data-testid="failed-numbers-empty">
        <Icon as={FiCheckCircle} boxSize={10} color="green.400" />
        <Text fontWeight="semibold">Every number is getting your texts</Text>
        <Text fontSize="sm" color={muted}>
          No failed deliveries on record{lastSynced ? ` · checked ${timeAgo(lastSynced)}` : ""}.
        </Text>
        <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={load} mt={2}>
          Refresh
        </Button>
      </Center>
    );
  }

  return (
    <VStack spacing={4} align="stretch" data-testid="failed-numbers-panel">
      <Flex justify="space-between" align="center" gap={2} wrap="wrap">
        <Box>
          <Text fontWeight="bold">Numbers your texts couldn't reach</Text>
          <Text fontSize="xs" color={muted}>
            Updated {timeAgo(lastSynced)} · syncs from SMS every 5 minutes
          </Text>
        </Box>
        <HStack spacing={2}>
          <Tooltip label="Download the list below as CSV" hasArrow>
            <Button size="sm" variant="outline" leftIcon={<FiDownload />} onClick={exportCsv} isDisabled={!visible.length}>
              Export
            </Button>
          </Tooltip>
          <Tooltip label="Reload" hasArrow>
            <IconButton
              size="sm"
              variant="outline"
              aria-label="Reload failed numbers"
              icon={<FiRefreshCw />}
              isLoading={state.status === "refreshing"}
              onClick={load}
            />
          </Tooltip>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2.5}>
        <StatTile
          label="Needs attention"
          value={summary.counts.attention}
          hint="Bad, opted out or failing"
          colorScheme="orange"
          icon={FiAlertTriangle}
          isActive={filter === "attention"}
          onClick={() => setFilter("attention")}
        />
        <StatTile
          label="Bad numbers"
          value={summary.counts.bad}
          hint="Fix the number on file"
          colorScheme="red"
          icon={FiPhoneOff}
          isActive={filter === "bad"}
          onClick={() => setFilter("bad")}
        />
        <StatTile
          label="Opted out"
          value={summary.counts.opt_out}
          hint="Do not text"
          colorScheme="purple"
          icon={FiUserX}
          isActive={filter === "opt_out"}
          onClick={() => setFilter("opt_out")}
        />
        <StatTile
          label="Failed texts"
          value={summary.failedTexts}
          hint={`${summary.counts.all} numbers affected`}
          colorScheme="gray"
          icon={FiMessageSquare}
        />
      </SimpleGrid>

      <Wrap spacing={2}>
        {FILTERS.map((f) => {
          const count = f.key === "attention" ? summary.counts.attention : summary.counts[f.key];
          const active = filter === f.key;
          return (
            <WrapItem key={f.key}>
              <Button
                size="xs"
                borderRadius="full"
                variant={active ? "solid" : "outline"}
                colorScheme={active ? "teal" : "gray"}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                <Badge ml={1.5} borderRadius="full" variant={active ? "solid" : "subtle"} colorScheme={active ? "whiteAlpha" : "gray"}>
                  {count}
                </Badge>
              </Button>
            </WrapItem>
          );
        })}
      </Wrap>

      <Stack direction={{ base: "column", md: "row" }} spacing={2}>
        <InputGroup size="sm" flex="2">
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            borderRadius="md"
            placeholder="Search patient, number, error code or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Select
          size="sm"
          borderRadius="md"
          flex="1"
          value={errorCode}
          onChange={(e) => setErrorCode(e.target.value)}
          aria-label="Filter by error code"
        >
          <option value="">All error codes</option>
          {codeOptions.map(({ code, numbers }) => (
            <option key={code} value={code}>
              {code} · {codes[code]?.description?.slice(0, 40) || "Unmapped"} ({numbers})
            </option>
          ))}
        </Select>
        {multiProvider && (
          <Select
            size="sm"
            borderRadius="md"
            flex="1"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            aria-label="Filter by provider"
          >
            <option value="">All providers ({allRows.length})</option>
            {rowProviders.map((s) => (
              <option key={s} value={s}>
                {providerName(s, home)} ({allRows.filter((r) => r.schema_name === s).length})
              </option>
            ))}
          </Select>
        )}
        <Select
          size="sm"
          borderRadius="md"
          flex="1"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort"
        >
          <option value="recent">Most recent failure</option>
          <option value="failures">Most failures</option>
        </Select>
        <DateRangeFilter value={dates} onChange={setDates} fieldLabel="last failure" />
      </Stack>

      {visible.length === 0 ? (
        <Center py={8} flexDirection="column" gap={2} textAlign="center">
          <Icon as={FiSearch} boxSize={7} color="gray.300" />
          <Text fontSize="sm" color={muted}>
            No numbers match these filters.
          </Text>
          <Button
            size="xs"
            variant="link"
            colorScheme="teal"
            onClick={() => {
              setFilter("all");
              setErrorCode("");
              setSearch("");
              setProvider("");
              setDates(EMPTY_RANGE);
            }}
          >
            Clear filters
          </Button>
        </Center>
      ) : (
        <Stack spacing={2}>
          <Text fontSize="xs" color={muted}>
            Showing {Math.min(shown, visible.length)} of {visible.length}
          </Text>
          {visible.slice(0, shown).map((row) => {
            const key = `${row.schema_name}:${row.phone_number}`;
            return (
              <NumberCard
                key={key}
                row={row}
                codes={codes}
                isOpen={openKey === key}
                onToggle={() => setOpenKey(openKey === key ? null : key)}
                loadTexts={loadTexts}
                providerLabel={multiProvider ? providerName(row.schema_name, home) : null}
              />
            );
          })}
          {shown < visible.length && (
            <Button size="sm" variant="ghost" onClick={() => setShown((n) => n + SHOW_STEP)}>
              Show {Math.min(SHOW_STEP, visible.length - shown)} more
            </Button>
          )}
        </Stack>
      )}
    </VStack>
  );
};

export default FailedNumbersPanel;
